import { unref, useContext } from "@nuxtjs/composition-api";
import { EVENT_PAGE_NAMES } from "~/data/pageNames";
import useRequestTenderPageModules from "~/composables/useRequestTenderPageModules";

export const DEFAULT_MODULE_VERSION_ID = "default";

export const WIZARD_ELEMENT_IDS = {
  NEXT_BUTTON: "WizardNextButton",
  NAME_INPUT: "WizardNameInput",
};

export const WIZARD_STEP_VIEWED_EVENT = "Wizard Step Viewed";

/**
 * Only a profession-specific override identifies an experiment arm; the
 * global config and the hardcoded fallback are both the "default" form.
 */
export function resolveTrackedModuleVersionId({ versionId, isOverride }) {
  return isOverride && versionId ? versionId : DEFAULT_MODULE_VERSION_ID;
}

/**
 * Shared PostHog properties for the request-tender wizard: which module
 * version was served and which profession it was resolved for (null when the
 * visitor picks the profession inside the wizard and no override applies yet).
 */
export function useWizardTrackingProperties() {
  const { versionId, isOverride, resolvedForProfessionId } =
    useRequestTenderPageModules();

  function getWizardTrackingProperties() {
    return {
      pagename: EVENT_PAGE_NAMES.QUOTE_WIZARD,
      moduleVersionId: resolveTrackedModuleVersionId({
        versionId: versionId.value,
        isOverride: isOverride.value,
      }),
      wizardProfessionId: resolvedForProfessionId.value ?? null,
    };
  }

  return { getWizardTrackingProperties };
}

/**
 * `Click` events for the request-tender wizard. `step` is the URL slot
 * (/ajanlatkeres/2 → 2) and `moduleSlug` is the module rendered in that slot,
 * which stays stable when the conductor reorders the steps.
 */
export function useTrackWizardClick({ currentStep, moduleSlug }) {
  const { $trackers } = useContext();
  const { getWizardTrackingProperties } = useWizardTrackingProperties();

  function trackWizardClick(elementId, data = {}) {
    $trackers.trackClick(elementId, {
      ...getWizardTrackingProperties(),
      step: unref(currentStep),
      moduleSlug: unref(moduleSlug),
      ...data,
    });
  }

  /**
   * Fired on every attempt to advance — button click or Enter — before
   * validation, so an attempt that goes nowhere is still recorded.
   */
  function trackNextAttempt({ trigger = "click", disabled = false } = {}) {
    trackWizardClick(WIZARD_ELEMENT_IDS.NEXT_BUTTON, { trigger, disabled });
  }

  return { trackWizardClick, trackNextAttempt };
}

/**
 * `Wizard Step Viewed`: one event per step the visitor actually sees, so the
 * served module version is known for visitors who never press Next.
 */
export function useTrackWizardStepViewed() {
  const { $trackers } = useContext();
  const { getWizardTrackingProperties } = useWizardTrackingProperties();

  function trackWizardStepViewed({ step, moduleSlug, totalSteps }) {
    if (!process.client) return;
    $trackers.trackEvent({
      title: WIZARD_STEP_VIEWED_EVENT,
      data: {
        ...getWizardTrackingProperties(),
        step,
        moduleSlug,
        totalSteps,
      },
    });
  }

  return { trackWizardStepViewed };
}
